/**
 * OIDC client create wizard (SUPER_ADMIN only). 3 steps + secret reveal.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError, type ErrorCode } from '@/api/errors';
import type {
  CreateOidcClientRequest,
  OidcAccessScope,
  PlatformDefaultsDto,
  ProductDto,
  TenantDto,
} from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SecretOnceDialog } from '@/components/SecretOnceDialog';
import { useCopy } from '@/hooks/useCopy';
import { useCurrentOperator } from '@/auth/AuthContext';
import { useSnackbar } from '@/components/Snackbar';

// SASATH-0075 (spec 0057) — login surface is one of two modes (the login-HOST
// axis). Platform-scoped (default, the GHL case) omits `loginTenantId`;
// tenant-scoped sends a single login tenant (any active tenant). INDEPENDENT of
// the access scope below — login mode and access scope are separate axes.
type LoginMode = 'platform' | 'tenant';

// Spec 0061 (SASATH-0078) — access scope is the per-product access boundary, a
// DISTINCT axis from the login mode. Platform-scoped (default) submits an empty
// `allowedProductIds` (all products' tenants); product-scoped submits the chosen
// product ids (only those products' tenants).
type AccessScope = 'platform' | 'product';

interface State {
  clientName: string;
  clientId: string;
  brandHost: string;
  integrationType: string;
  redirectUris: string[];
  postLogoutRedirectUris: string[];
  scopes: string[];
  accessScope: AccessScope;
  allowedProductIds: number[];
  loginMode: LoginMode;
  loginTenantId: number | null;
}

// 400 `error.code`s surfaced on the relevant wizard field instead of the generic
// "Something went wrong" banner (spec 0057). The message comes from
// `oidc.create.errors.<code>`. Spec 0061 dropped LOGIN_TENANT_NOT_ALLOWED (the
// "login tenant ∈ allowed tenants" coupling no longer exists once access scope is
// per-product) and ALLOWED_TENANTS_REQUIRED (empty = Platform is valid; the
// access-boundary requirement is now the client-side ≥1-product rule).
const FIELD_ERROR_CODES: readonly ErrorCode[] = [
  'LOGIN_HOSTNAME_REQUIRED',
  'REDIRECT_URI_INVALID',
  'BRAND_HOST_REQUIRED',
];

/** Login-tenant search: idle time before the typed query goes to the server. */
const TENANT_SEARCH_DEBOUNCE_MS = 300;
/** Login-tenant search: matches requested per query (a picker, not a browser). */
const TENANT_SEARCH_SIZE = 20;

export function OidcClientCreate() {
  const { t } = useCopy();
  const navigate = useNavigate();
  const operator = useCurrentOperator();
  const snack = useSnackbar();
  const [active, setActive] = useState(0);
  // SASADM-0108 — the login-tenant picker searches the server on demand. The
  // three pieces are the current matches, the raw text typed into the field, and
  // whether a search is in flight; the chosen tenant is held as the DTO (not just
  // its id) so its label survives a later search that doesn't include it.
  const [tenantOptions, setTenantOptions] = useState<TenantDto[]>([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantLoading, setTenantLoading] = useState(false);
  const [loginTenant, setLoginTenant] = useState<TenantDto | null>(null);
  // What the server is actually asked for. Only leading/trailing whitespace is
  // dropped — `%`, `_` and `\` are matched literally by the server, so escaping
  // them would corrupt the search.
  const tenantQuery = tenantSearch.trim();
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [defaults, setDefaults] = useState<PlatformDefaultsDto | null>(null);
  const [state, setState] = useState<State>({
    clientName: '',
    clientId: '',
    brandHost: '',
    integrationType: 'GHL_AGENCY',
    redirectUris: [],
    postLogoutRedirectUris: [],
    scopes: ['openid', 'profile', 'email'],
    accessScope: 'platform',
    allowedProductIds: [],
    loginMode: 'platform',
    loginTenantId: null,
  });
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdAccessScope, setCreatedAccessScope] = useState<OidcAccessScope | null>(null);
  const [createdProductIds, setCreatedProductIds] = useState<number[]>([]);

  // SASADM-0108 — the picker no longer full-fetches the tenant table on mount: it
  // asks the server for matches once the operator stops typing. An empty query is
  // never sent (there is nothing to search for), so the field starts with no
  // options. `current` is the out-of-order guard: a slower earlier response is
  // dropped rather than overwriting a newer one. An error degrades to no options,
  // exactly as the removed full fetch degraded to an empty list.
  useEffect(() => {
    if (!tenantQuery) return;
    let current = true;
    const timer = setTimeout(() => {
      setTenantLoading(true);
      void apiAdmin.tenants
        .listPaged({ q: tenantQuery, size: TENANT_SEARCH_SIZE, status: 'ACTIVE' })
        .then((res) => {
          if (current) setTenantOptions(res.content);
        })
        .catch(() => {
          if (current) setTenantOptions([]);
        })
        .finally(() => {
          if (current) setTenantLoading(false);
        });
    }, TENANT_SEARCH_DEBOUNCE_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [tenantQuery]);

  useEffect(() => {
    // The product picker source (spec 0061). Degrade like the tenants fetch on
    // any error — an empty list just leaves the picker with no options.
    void apiAdmin.products
      .list()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    // Spec 0057 — `authHostname` is the brand-host placeholder (default-with-
    // override). Null until saas-auth SASATH-0073 ships → no placeholder, blank
    // still omits. Degrade like the tenants fetch above on any error.
    void apiAdmin.platformDefaults
      .get()
      .then(setDefaults)
      .catch(() => setDefaults(null));
  }, []);

  if (!operator?.isSuperAdmin) {
    return (
      <Alert severity="warning">{t('errors.superAdminRequired', 'Requires Super admin.')}</Alert>
    );
  }

  async function submit() {
    setPending(true);
    setError(null);
    const payload: CreateOidcClientRequest = {
      clientName: state.clientName,
      clientId: state.clientId,
      integrationType: state.integrationType,
      redirectUris: state.redirectUris,
      postLogoutRedirectUris: state.postLogoutRedirectUris,
      scopes: state.scopes,
      // Access scope (spec 0061): platform-scoped submits an empty list (all
      // products); product-scoped submits the chosen product ids.
      allowedProductIds: state.accessScope === 'product' ? state.allowedProductIds : [],
    };
    // Brand host: submit a value ONLY on override; blank → omit → NULL →
    // follows public-origin (never submit the displayed default placeholder).
    const brand = state.brandHost.trim();
    if (brand) payload.brandHost = brand;
    // Login mode: platform-scoped omits `loginTenantId`; tenant-scoped sends the
    // chosen login tenant (any active tenant — independent of the access scope).
    if (state.loginMode === 'tenant' && state.loginTenantId != null) {
      payload.loginTenantId = state.loginTenantId;
    }
    try {
      const res = await apiAdmin.oidcClients.create(payload);
      setCreatedSecret(res.clientSecret);
      setCreatedId(res.clientDbId);
      setCreatedAccessScope(res.accessScope);
      setCreatedProductIds(res.allowedProductIds ?? []);
      snack.success(t('snackbar.oidcCreated', 'OIDC client created.'));
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  // Field-level mapping of the 400 codes (spec 0057): show the specific message
  // on the relevant field instead of the generic banner. Unmapped errors still
  // fall through to <ErrorBanner>.
  const fieldErrorCode = error && FIELD_ERROR_CODES.includes(error.code) ? error.code : null;
  const fieldErrorMessage = fieldErrorCode
    ? t(`oidc.create.errors.${fieldErrorCode}`, fieldErrorCode)
    : null;
  const loginTenantErrored = fieldErrorCode === 'LOGIN_HOSTNAME_REQUIRED';

  // Access scope (spec 0061): the picker source, sorted by group then name so the
  // Autocomplete's `groupBy` renders contiguous group headers.
  const productOptions = [...products].sort(
    (a, b) => a.productGroup.localeCompare(b.productGroup) || a.displayName.localeCompare(b.displayName),
  );
  const selectedProducts = products.filter((p) => state.allowedProductIds.includes(p.id));
  const productScopeEmpty = state.accessScope === 'product' && state.allowedProductIds.length === 0;

  // Login mode is INDEPENDENT of access scope: the login tenant can be any active
  // tenant (spec 0061 decoupling — no longer constrained to the access boundary).
  // The server applies that ACTIVE filter now (SASADM-0108). Results belong to
  // the query that fetched them, so clearing the field drops them from view (and
  // ends any "searching" state) without the effect having to clear state. The
  // chosen tenant is always an option, even once a later search no longer returns
  // it — otherwise the field would blank out the moment the operator typed again.
  const tenantResults = tenantQuery ? tenantOptions : [];
  const tenantSearching = tenantLoading && tenantQuery !== '';
  const loginTenantOptions =
    loginTenant && !tenantResults.some((tn) => tn.id === loginTenant.id)
      ? [loginTenant, ...tenantResults]
      : tenantResults;
  // Product-scoped requires ≥1 product; tenant-scoped requires a login tenant.
  const submitDisabled =
    pending ||
    productScopeEmpty ||
    (state.loginMode === 'tenant' && state.loginTenantId == null);

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.oidcClients', 'OIDC clients'), to: '/reference/oidc-clients' },
          { label: t('oidc.create.title', 'Create OIDC client') },
        ]}
      />
      <Typography variant="h1" gutterBottom>
        {t('oidc.create.title', 'Create OIDC client')}
      </Typography>
      <Stepper activeStep={active} sx={{ mb: 3 }} alternativeLabel>
        <Step>
          <StepLabel>{t('oidc.create.step1', 'Identity')}</StepLabel>
        </Step>
        <Step>
          <StepLabel>{t('oidc.create.step2', 'Endpoints & scopes')}</StepLabel>
        </Step>
        <Step>
          <StepLabel>{t('oidc.create.step3', 'Access scope')}</StepLabel>
        </Step>
      </Stepper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        {fieldErrorCode ? (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="field-error">
            {fieldErrorMessage}
          </Alert>
        ) : (
          <ErrorBanner error={error} />
        )}
        {active === 0 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                required
                label="Client name"
                value={state.clientName}
                onChange={(e) => setState({ ...state, clientName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                required
                label="Client ID"
                value={state.clientId}
                onChange={(e) => setState({ ...state, clientId: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('oidc.create.brandHost', 'Brand host')}
                value={state.brandHost}
                placeholder={defaults?.authHostname || undefined}
                helperText={t(
                  'oidc.create.brandHostHelper',
                  'Defaults to the platform auth host. Override only if this client authenticates on a different brand host.',
                )}
                error={fieldErrorCode === 'BRAND_HOST_REQUIRED'}
                onChange={(e) => setState({ ...state, brandHost: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('oidc.create.integrationType', 'Integration type')}
                value={state.integrationType}
                onChange={(e) => setState({ ...state, integrationType: e.target.value })}
              />
            </Grid>
          </Grid>
        )}
        {active === 1 && (
          <Stack spacing={2}>
            <ChipList
              label="Redirect URIs"
              values={state.redirectUris}
              onChange={(v) => setState({ ...state, redirectUris: v })}
            />
            <ChipList
              label="Post-logout redirect URIs"
              values={state.postLogoutRedirectUris}
              onChange={(v) => setState({ ...state, postLogoutRedirectUris: v })}
            />
            <ChipList
              label="Scopes"
              values={state.scopes}
              onChange={(v) => setState({ ...state, scopes: v })}
            />
          </Stack>
        )}
        {active === 2 && (
          <Stack spacing={2}>
            <FormControl>
              <FormLabel id="oidc-access-scope-label">
                {t('oidc.create.accessScope', 'Access scope')}
              </FormLabel>
              <RadioGroup
                aria-labelledby="oidc-access-scope-label"
                value={state.accessScope}
                onChange={(e) =>
                  setState((s) => ({ ...s, accessScope: e.target.value as AccessScope }))
                }
              >
                <FormControlLabel
                  value="platform"
                  control={<Radio />}
                  label={t('oidc.create.accessScopePlatform', 'Platform-scoped (all products)')}
                />
                <FormControlLabel
                  value="product"
                  control={<Radio />}
                  label={t('oidc.create.accessScopeProduct', 'Product-scoped')}
                />
              </RadioGroup>
              <FormHelperText component="span">
                {state.accessScope === 'platform'
                  ? t(
                      'oidc.create.accessScopePlatformHelp',
                      'All products and their tenants can use this client. The common GHL case.',
                    )
                  : t(
                      'oidc.create.accessScopeProductHelp',
                      "Only the chosen products' tenants can use this client.",
                    )}
              </FormHelperText>
            </FormControl>
            {state.accessScope === 'product' ? (
              <Box>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={productOptions}
                  value={selectedProducts}
                  groupBy={(option) => option.productGroup}
                  getOptionLabel={(option) => option.displayName}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_e, value) =>
                    setState((s) => ({ ...s, allowedProductIds: value.map((p) => p.id) }))
                  }
                  renderOption={(props, option, { selected }) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Checkbox sx={{ mr: 1 }} checked={selected} />
                        {`${option.displayName} (${option.slug})`}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('oidc.create.productPicker', 'Products')}
                      placeholder={t('oidc.create.productPickerPlaceholder', 'Select products')}
                      error={productScopeEmpty}
                    />
                  )}
                />
                {productScopeEmpty ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t('oidc.create.selectAtLeastOneProduct', 'Select at least one product.')}
                  </Alert>
                ) : null}
              </Box>
            ) : null}
            <FormControl>
              <FormLabel id="oidc-login-mode-label">
                {t('oidc.create.loginMode', 'Login mode')}
              </FormLabel>
              <RadioGroup
                aria-labelledby="oidc-login-mode-label"
                value={state.loginMode}
                onChange={(e) => {
                  const loginMode = e.target.value as LoginMode;
                  setState((s) => ({
                    ...s,
                    loginMode,
                    // Platform-scoped never carries a login tenant.
                    loginTenantId: loginMode === 'platform' ? null : s.loginTenantId,
                  }));
                  if (loginMode === 'platform') setLoginTenant(null);
                }}
              >
                <FormControlLabel
                  value="platform"
                  control={<Radio />}
                  label={t('oidc.create.loginModePlatform', 'Platform-scoped (default)')}
                />
                <FormControlLabel
                  value="tenant"
                  control={<Radio />}
                  label={t('oidc.create.loginModeTenant', 'Tenant-scoped')}
                />
              </RadioGroup>
              <FormHelperText component="span">
                {state.loginMode === 'platform'
                  ? t(
                      'oidc.create.loginModePlatformHelp',
                      'Auth across the allowed tenants on the platform login host — no single login tenant. The common GHL case.',
                    )
                  : t('oidc.create.loginModeTenantHelp', "A specific tenant's users log in to that tenant.")}
              </FormHelperText>
            </FormControl>
            {state.loginMode === 'tenant' ? (
              <Autocomplete
                fullWidth
                options={loginTenantOptions}
                value={loginTenant}
                loading={tenantSearching}
                // The server already filtered by `q`; filtering again on the
                // client would only hide matches it deliberately returned.
                filterOptions={(options) => options}
                getOptionLabel={(option) => `${option.name} (${option.slug})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loadingText={t('oidc.create.loginTenantSearching', 'Searching…')}
                noOptionsText={
                  tenantQuery
                    ? t('oidc.create.loginTenantNoMatches', 'No matching active tenants.')
                    : t('oidc.create.loginTenantSearchHint', 'Type to search active tenants.')
                }
                // Only what the operator actually typed is a query. MUI also
                // fires this when an option is selected (`selectOption`, with the
                // option's label) and on blur (`blur`/`reset`) — searching for
                // `"Acme (acme)"` would be a round-trip for a string nobody typed
                // and which the server's contains-match cannot match. `clear` is
                // let through so emptying the field also empties the options.
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input' || reason === 'clear') setTenantSearch(value);
                }}
                onChange={(_e, value) => {
                  setLoginTenant(value);
                  setState((s) => ({ ...s, loginTenantId: value?.id ?? null }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={t('oidc.create.loginTenant', 'Login tenant')}
                    placeholder={t('oidc.create.loginTenantPlaceholder', 'Select a login tenant')}
                    error={loginTenantErrored}
                    helperText={t(
                      'oidc.create.loginTenantHelper',
                      'The tenant whose login host this client uses.',
                    )}
                  />
                )}
              />
            ) : null}
          </Stack>
        )}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            mt: 3,
          }}
        >
          <Button disabled={active === 0 || pending} onClick={() => setActive(active - 1)}>
            {t('common.back', 'Back')}
          </Button>
          {active < 2 ? (
            <Button variant="contained" onClick={() => setActive(active + 1)}>
              {t('common.next', 'Next')}
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={submitDisabled}
              onClick={() => void submit()}
              startIcon={pending ? <CircularProgress size={16} /> : null}
            >
              {t('common.create', 'Create')}
            </Button>
          )}
        </Stack>
      </Paper>
      <SecretOnceDialog
        open={createdSecret !== null}
        secret={createdSecret ?? ''}
        extras={
          createdAccessScope ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t('oidc.create.accessScope', 'Access scope')}
              </Typography>
              {createdAccessScope === 'PRODUCT' && createdProductIds.length > 0 ? (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                  {createdProductIds.map((id) => {
                    const product = products.find((p) => p.id === id);
                    return (
                      <Chip
                        key={id}
                        label={product ? `${product.displayName} (${product.slug})` : String(id)}
                        size="small"
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('oidc.create.accessScopePlatform', 'Platform-scoped (all products)')}
                </Typography>
              )}
            </Box>
          ) : null
        }
        onAcknowledge={() => {
          setCreatedSecret(null);
          if (createdId) navigate(`/reference/oidc-clients/${createdId}`);
          else navigate('/reference/oidc-clients');
        }}
      />
    </Box>
  );
}

interface ChipListProps {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
}

function ChipList({ label, values, onChange }: ChipListProps) {
  const [input, setInput] = useState('');
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {values.map((v) => (
          <Chip
            key={v}
            label={v}
            onDelete={() => onChange(values.filter((x) => x !== v))}
            size="small"
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input.trim()) {
              e.preventDefault();
              if (!values.includes(input.trim())) onChange([...values, input.trim()]);
              setInput('');
            }
          }}
        />
        <Button
          onClick={() => {
            if (input.trim() && !values.includes(input.trim())) {
              onChange([...values, input.trim()]);
              setInput('');
            }
          }}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}
